import { isUndefined } from "util";

/**
 * Applys a validator to the given value if the passed in value is not undefined, and then
 * returns either true or false. If passed in value is undefined, will return undefined .
 *
 * @example
 * let isAlpha = (value: string) => /[a-zA-Z]/gi.test(value)
 * let result = isStringValid('123', isAlpha) // Result will be false, invalid
 *
 * // If result === true, it passed validation
 * // If result === false, it failed validation
 * // If result === undefined, validation was skipped, because length of string === 0 or a non-string was passed in
 *
 * @param value
 * @param validator
 */

export function isStringValid(
  value: string | undefined,
  validator: (value: string | undefined) => boolean
): boolean | undefined | undefined {
  if (typeof value === "string" && value.length > 0) return validator(value);
}

/**
 * Account name is between 4-20 chars and contains only letters (no spaces)
 * @param account
 */
export function validAccountName(account: string | undefined): boolean {
  const re = /^[A-Za-z]+$/;
  return (
    typeof account === "string" &&
    re.test(account) &&
    account.length > 4 &&
    account.length < 20
  );
}

/**
 * Analysis name is between 4-20 chars and contains only alphanumeric (no spaces)
 * @param name
 */
export function validAnalysisName(analysis: string | undefined): boolean {
  const re = /^[a-zA-Z0-9]+$/;
  return (
    typeof analysis === "string" &&
    re.test(analysis) &&
    analysis.length > 4 &&
    analysis.length < 20
  );
}

/**
 * Username is between 5-16 chars long
 * No _ or . at the beginning
 * No __ or _. or ._ or .. inside
 * Only allow alphanumeric, period (.), and underscore ( _ ) chars
 * Must end in an alphanumeric character
 *
 * Source: https://stackoverflow.com/a/12019115/11406007
 *
 * @param username
 */
export function validUsername(username: string | undefined): boolean {
  const re = /^(?=.{5,16}$)(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+([a-zA-Z0-9])$/;
  return typeof username === "string" && re.test(username);
}

/**
 * Password must be between 10-20 chars long
 * Contain at least 1 of all: lowercase, uppercase, number, symbol
 * @param password
 */
export function validPassword(password: string | undefined): boolean {
  const re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{10,20}$/;
  return typeof password === "string" && re.test(password);
}

/**
 * Email must contain: an @ symbol and a . after the @ symbol
 * @param email
 */
export function validEmail(email: string | undefined): boolean {
  var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return typeof email === "string" && re.test(email);
}

/**
 * First name contains only letters and is between 2-30 chars
 * @param name
 */
export function validFirstName(name: string | undefined): boolean {
  var re = /^[-a-zA-Z ]{2,30}$/;
  return typeof name === "string" && re.test(name);
}

/**
 * Last name contains only letters and is between 2-30 chars
 * @param name
 */
export function validLastName(name: string | undefined) {
  var re = /^[-a-zA-Z ]{2,30}$/;
  return typeof name === "string" && re.test(name);
}

/**
 * Team name is between 4-20 chars and contains only letters (no spaces)
 * @param team
 */
export function validTeamName(team: string | undefined): boolean {
  const re = /^[A-Za-z]+$/;
  return (
    typeof team === "string" &&
    re.test(team) &&
    team.length > 4 &&
    team.length < 20
  );
}

export function isNotEmpty(value: any) {
  if (value) {
    return value.length > 0;
  }
  return false;
}

export const getInputValidity = (
  property: string | undefined,
  validationFunction: (param: string | undefined) => boolean
) => {
  const isValid = isStringValid(property, validationFunction);
  if (!isUndefined(isValid) && isValid) return "valid";
  if (!isUndefined(isValid) && !isValid) return "invalid";
};
